import { FilledTextFieldProps, TextField as MuiTextField } from '@material-ui/core'
import React, { useEffect } from 'react'
import { Controller, UseControllerOptions, useFormContext } from 'react-hook-form'

import { SelectFieldOption } from '../../lib/form'

export interface SelectFieldProps
  extends Omit<
    FilledTextFieldProps,
    'error' | 'inputRef' | 'onBlur' | 'onChange' | 'value' | 'variant'
  > {
  fsExclude?: boolean
  inputMode?: Exclude<FilledTextFieldProps['inputProps'], undefined>['inputMode']
  label: string
  name: string
  onChange?(selectedValue: unknown): void
  rules?: UseControllerOptions['rules']
  options: SelectFieldOption[]
}

const SelectField: React.FC<SelectFieldProps> = ({
  defaultValue = '',
  fullWidth = true,
  id,
  inputProps = {},
  InputProps = {},
  label,
  name,
  onChange,
  options,
  rules,
  SelectProps = {},
  InputLabelProps = {},
  ...other
}) => {
  const { errors, control, watch } = useFormContext()
  const selectedValue = watch(name)

  useEffect(() => {
    if (onChange) {
      onChange(selectedValue)
    }
  }, [onChange, selectedValue])

  return (
    <Controller
      render={({ name: controlledName, onBlur, onChange: controlledOnChange, ref, value }) => (
        <MuiTextField
          fullWidth={fullWidth}
          id={id || controlledName}
          inputProps={inputProps}
          InputProps={{
            disableUnderline: true,
            ...InputProps,
          }}
          InputLabelProps={{
            ...InputLabelProps,
            // workaround bug with label not shrinking after calling `setValue()`
            shrink: 'shrink' in InputLabelProps ? InputLabelProps.shrink : !!value,
          }}
          label={label}
          {...other}
          error={!!errors[controlledName]}
          inputRef={ref}
          name={controlledName}
          onBlur={onBlur}
          onChange={controlledOnChange}
          {...(rules?.required === true && { required: true })}
          select
          SelectProps={{
            native: true,
            ...SelectProps,
          }}
          value={value}
          variant="filled"
        >
          <option aria-label="None" value="" />
          {options.map((option) => (
            // Derive key from value and label in case option values are repeated
            <option key={`${option.value}:${option.label}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </MuiTextField>
      )}
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={rules}
    />
  )
}

export default SelectField
